import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'contexts/AuthContext'

import ECodeStep from './components/ECodeStep'
import OTPStep from './components/OTPStep'
import PasswordStep from './components/PasswordStep'

import {
  sendOtp,
  resendOtp,
  verifyOtp,
  loginWithPassword,
} from 'api/auth'

type LoginStep = 'ecode' | 'otp' | 'password'

const LoginPage: React.FC = () => {
  const [currentStep, setCurrentStep] =
    useState<LoginStep>('ecode')

  const [ecode, setEcode] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null)

  const navigate = useNavigate()
  const { login } = useAuth()

  const navigateToHome = () => {
    navigate('/')
  }

  const handleOtpLogin = async (employeeCode: string) => {
    setLoading(true)
    setError('')
    try {
      const response: any = await sendOtp(employeeCode)

      const phoneMatch = response?.message?.match(/(\d{4})$/)
      const extractedPhone = phoneMatch ? phoneMatch[1] : null

      setPhoneNumber(extractedPhone)
      setEcode(employeeCode)
      setCurrentStep('otp')
    } catch (err: any) {
      setError(err?.message || 'Failed to send OTP')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async (otp: string) => {
    setLoading(true)
    setError('')
    try {
      const authData = await verifyOtp(ecode, otp)
      await login(authData)
      navigateToHome()
    } catch (err: any) {
      setError(err?.message || 'Invalid OTP')
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordLogin = async (employeeCode: string, password: string) => {
    setLoading(true)
    setError('')
    try {
      const authData = await loginWithPassword(employeeCode, password)
      await login(authData)

      navigateToHome()
    } catch (err: any) {
      setError(err?.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const handleResendOtp = async () => {
    try {
      await resendOtp(ecode)
    } catch (err: any) {
      setError(
        err?.message || 'Failed to resend OTP'
      )
    }
  }

  const goToPasswordLogin = (employeeCode: string) => {
    setEcode(employeeCode)
    setError('')
    setCurrentStep('password')
  }

  const goBackToEcode = () => {
    setCurrentStep('ecode')
    setEcode('')
    setPhoneNumber(null)
    setError('')
  }

  const renderStep = () => {
    switch (currentStep) {
      case 'ecode':
        return (
          <ECodeStep
            onOTPLogin={handleOtpLogin}
            onPasswordLogin={
              goToPasswordLogin
            }
            isLoading={loading}
            error={error}
          />
        )

      case 'otp':
        return (
          <OTPStep
            ecode={ecode}
            phoneNumber={phoneNumber}
            onVerify={handleVerifyOtp}
            onBack={goBackToEcode}
            onResendOTP={
              handleResendOtp
            }
            isLoading={loading}
            error={error}
          />
        )

      case 'password':
        return (
          <PasswordStep
            ecode={ecode}
            onLogin={
              handlePasswordLogin
            }
            onBack={goBackToEcode}
            isLoading={loading}
            error={error}
          />
        )

      default:
        return null
    }
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <aside className="relative hidden lg:block">
        <div
          className="absolute inset-0 bg-gray-100 bg-center bg-cover"
          style={{
            backgroundImage:
              "url('/images/login-side.png')",
          }}
        />
        <div className="relative z-10 flex h-full flex-col justify-between p-8 bg-white/30 backdrop-blur-[1px]">
          <div className="flex items-center gap-2">
            <img
              src="/images/white-logo.png"
              alt="MRC Logistics Logo"
              className="h-12 w-auto"
            />
          </div>
        </div>
      </aside>

      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          {renderStep()}
        </div>
      </div>
    </div>
  )
}

export default LoginPage